import { api } from 'pages/APIs';
import AbstractApiService from 'src/services/abstractApiService';
import type {
  DocumentTypeKey,
  ExtractionResult,
  DocumentCreationRequest,
  DocumentCreationResponse,
  AutoProcessConfig,
  AutoProcessResult,
} from 'src/types/docnetEmail';

/**
 * API Service for Email Document Creation
 *
 * Handles operations for the /email (Mailroom) page:
 * - Extract data from emails
 * - Create documents from extracted data
 * - Auto-process emails with high confidence
 */
export default class EmailDocumentApiService extends AbstractApiService {
  public constructor({}) {
    super({});
  }

  // ============================================
  // EXTRACTION
  // ============================================

  /**
   * Extract document data from an email
   * Returns classified document type and extracted fields
   */
  public async extractFromEmail(emailId: number): Promise<ExtractionResult> {
    return super.post(`${api.email_extract}/${emailId}`, {});
  }

  /**
   * Get previously stored extraction result for an email
   */
  public async getExtractionResult(
    emailId: number,
  ): Promise<ExtractionResult | null> {
    return super.get(`${api.email_extraction_result}/${emailId}`);
  }

  /**
   * Re-extract with a different document type
   * Useful when user changes the document type selection
   */
  public async reExtractWithType(
    emailId: number,
    docTypeKey: DocumentTypeKey,
  ): Promise<ExtractionResult> {
    return super.post(`${api.email_extract}/${emailId}`, {
      doc_type_key: docTypeKey,
      force_reclassify: true,
    });
  }

  // ============================================
  // DOCUMENT CREATION
  // ============================================

  /**
   * Create a document from extracted email data
   * This is the main action when user clicks "Create Document"
   */
  public async createDocument(
    request: DocumentCreationRequest,
  ): Promise<DocumentCreationResponse> {
    return super.post(api.email_document_create, request);
  }

  /**
   * Create document as draft for review
   */
  public async createDraftDocument(
    request: Omit<DocumentCreationRequest, 'status'>,
  ): Promise<DocumentCreationResponse> {
    return super.post(api.email_document_create, {
      ...request,
      status: 'draft',
    });
  }

  /**
   * Create and immediately process document
   */
  public async createAndProcessDocument(
    request: Omit<DocumentCreationRequest, 'status'>,
  ): Promise<DocumentCreationResponse> {
    return super.post(api.email_document_create, {
      ...request,
      status: 'process',
    });
  }

  // ============================================
  // AUTO-PROCESSING
  // ============================================

  /**
   * Auto-process multiple emails based on confidence thresholds
   * Only processes emails where all required fields meet threshold
   */
  public async autoProcessEmails(
    emailIds: number[],
    config?: AutoProcessConfig,
  ): Promise<AutoProcessResult> {
    return super.post(api.email_auto_process, {
      email_ids: emailIds,
      config: config ?? {
        enabled: true,
        min_confidence_threshold: 85,
        require_all_fields_above_threshold: true,
        excluded_fields: [],
      },
    });
  }

  /**
   * Preview auto-process results without actually creating documents
   * Useful for showing user what will be auto-processed
   */
  public async previewAutoProcess(
    emailIds: number[],
    config?: AutoProcessConfig,
  ): Promise<{
    would_process: number[];
    would_skip: number[];
    reasons: Record<number, string>;
  }> {
    return super.post(`${api.email_auto_process}/preview`, {
      email_ids: emailIds,
      config: config ?? {
        enabled: true,
        min_confidence_threshold: 85,
        require_all_fields_above_threshold: true,
        excluded_fields: [],
      },
    });
  }

  // ============================================
  // VALIDATION HELPERS
  // ============================================

  /**
   * Validate customer ID exists in master data
   */
  public async validateCustomerId(customerId: string): Promise<{
    valid: boolean;
    customer_name?: string;
    error?: string;
  }> {
    return super.get(
      `${api.email_document_create}/validate-customer/${customerId}`,
    );
  }

  /**
   * Validate item code exists in master data
   */
  public async validateItemCode(itemCode: string): Promise<{
    valid: boolean;
    item_name?: string;
    unit_of_measure?: string;
    unit_price?: number;
    error?: string;
  }> {
    return super.get(
      `${api.email_document_create}/validate-item/${itemCode}`,
    );
  }

  /**
   * Search customers in master data
   */
  public async searchCustomers(
    query: string,
    limit = 10,
  ): Promise<
    Array<{
      customer_id: string;
      customer_name: string;
      email?: string;
    }>
  > {
    return super.get(
      `${api.email_document_create}/search-customers?q=${encodeURIComponent(query)}&limit=${limit}`,
    );
  }

  /**
   * Search items in master data
   */
  public async searchItems(
    query: string,
    limit = 10,
  ): Promise<
    Array<{
      item_code: string;
      item_name: string;
      unit_of_measure: string;
      unit_price: number;
    }>
  > {
    return super.get(
      `${api.email_document_create}/search-items?q=${encodeURIComponent(query)}&limit=${limit}`,
    );
  }
}
